import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SNAPSHOTS, PILLAR_ANCHORS, CASE_STUDIES } from "@/data/content";
import { SNAPSHOT } from "@/constants/testIds";

const FILTERS = [
    { slug: "all", label: "All work", testId: SNAPSHOT.filterAll },
    { slug: "forex", label: "Forex Brokers", testId: SNAPSHOT.filterForex },
    { slug: "fintech", label: "Fintech", testId: SNAPSHOT.filterCrypto },
    { slug: "trading-firm", label: "Trading Firms", testId: SNAPSHOT.filterPropFirm },
    { slug: "branding", label: "Branding", testId: SNAPSHOT.filterBranding }
];

export default function Snapshot() {
    const [filter, setFilter] = useState("all");
    const { hash } = useLocation();

    // Smooth-scroll to the pillar section when arriving with a hash from /what-we-do.
    useEffect(() => {
        if (!hash) return;
        const id = hash.replace("#", "");
        // Wait a tick so the section is mounted after route change.
        const t = setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 60);
        return () => clearTimeout(t);
    }, [hash]);

    const items = useMemo(
        () => (filter === "all" ? SNAPSHOTS : SNAPSHOTS.filter((s) => s.category === filter)),
        [filter]
    );

    return (
        <div>
            {/* HERO */}
            <section className="container-x pt-24 pb-16">
                <p className="overline mb-8">Chapter 03 · Snapshot</p>
                <Reveal>
                    <h1
                        className="font-display leading-[0.9] tracking-tighter"
                        style={{ fontSize: "clamp(2rem, 6vw, 5rem)" }}
                    >
                        Selected work.{" "}
                        <span className="italic text-brand-primary">Selected wins.</span>
                    </h1>
                </Reveal>
                <Reveal delay={120} className="mt-12 max-w-3xl">
                    <p className="text-xl text-brand-frost/85 leading-relaxed">
                        A snapshot of partnerships across forex brokers, fintech brands and trading firms. The campaigns, brand systems and acquisition engines we&apos;ve shipped with our clients.
                    </p>
                    <p className="mt-6 text-sm text-brand-primary/70 leading-relaxed max-w-2xl">
                        Most of our work is covered by client NDAs, so results here are shown without naming the client. Where a platform has given us permission to be named, we do.
                    </p>
                </Reveal>
            </section>

            {/* FILTERS */}
            <section className="border-y border-brand-primary/10 sticky top-20 z-30 bg-brand-bg/85 backdrop-blur-xl">
                <div className="container-x flex items-center gap-2 sm:gap-4 overflow-x-auto no-scrollbar py-5">
                    {FILTERS.map((f) => {
                        const active = filter === f.slug;
                        return (
                            <button
                                key={f.slug}
                                data-testid={f.testId}
                                onClick={() => setFilter(f.slug)}
                                className={`shrink-0 px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-all duration-300 ${
                                    active
                                        ? "bg-brand-primary text-brand-bg border-brand-primary"
                                        : "border-brand-primary/30 text-brand-primary hover:text-brand-mist hover:border-brand-primary"
                                }`}
                            >
                                {f.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* GRID */}
            <section className="py-16 lg:py-24">
                <div data-testid={SNAPSHOT.grid} className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6">
                    {items.map((s, i) => {
                        const span = i % 5 === 0 || i % 5 === 3 ? "lg:col-span-7" : "lg:col-span-5";
                        return (
                            <Reveal
                                key={s.slug}
                                delay={(i % 4) * 80}
                                data-testid={SNAPSHOT.card(s.slug)}
                                className={span}
                            >
                                <div className="group relative border border-brand-primary/10 bg-brand-surface flex flex-col lg:block h-full">
                                    {/* image: only the image is clipped, never the text */}
                                    <div className="relative overflow-hidden">
                                        <div
                                            className="aspect-[16/10] w-full bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-110"
                                            style={{ backgroundImage: `url(${s.image})` }}
                                        />
                                        <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-brand-bg via-brand-bg/40 to-transparent" />
                                    </div>
                                    {/* text: stacked on mobile/tablet, overlaid on desktop */}
                                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-end lg:absolute lg:inset-0">
                                        <div className="flex items-center justify-between mb-4 gap-4">
                                            <p className="overline">{s.tag}</p>
                                            <ArrowUpRight className="shrink-0 w-5 h-5 text-brand-primary/70 transition-all duration-500 group-hover:text-brand-primary group-hover:translate-x-1 group-hover:-translate-y-1" />
                                        </div>
                                        <h3 className="font-display h-display-sm text-brand-mist mb-3">{s.title}</h3>
                                        <p className="text-brand-frost/85 leading-relaxed mb-5 max-w-xl">{s.excerpt}</p>
                                        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-brand-primary/15 pt-4">
                                            <span className="text-brand-frost/80 text-sm">{s.client}</span>
                                            <span className="font-mono text-xs text-brand-primary uppercase tracking-widest">{s.metric}</span>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>

                {items.length === 0 && (
                    <div className="container-x py-24 text-center">
                        <p className="text-brand-frost/70">No projects in this category yet, but plenty in the pipeline.</p>
                    </div>
                )}
            </section>

            {/* PILLAR-ANCHORED PROOF SECTIONS
                Deep links from /what-we-do?see-snapshot land inside these blocks. */}
            <section className="border-t border-brand-primary/10 bg-brand-surface/30 py-16 lg:py-24">
                <div className="container-x space-y-16 lg:space-y-24">
                    <Reveal className="max-w-3xl">
                        <p className="overline mb-6">Proof by service pillar</p>
                        <h2 className="font-display h-display-md">
                            Case studies and testimonials, grouped by the service that shipped them.
                        </h2>
                    </Reveal>
                    {PILLAR_ANCHORS.map((p) => {
                        const cases = SNAPSHOTS.filter((s) => s.pillar === p.anchor);
                        return (
                            <div key={p.anchor} id={p.anchor} className="scroll-mt-28 border-t border-brand-primary/10 pt-12 lg:pt-16">
                                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                                    <h3 className="font-display h-display-sm text-brand-mist">
                                        {p.title}
                                    </h3>
                                    <Link
                                        to="/what-we-do"
                                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-primary hover:text-brand-mist transition-colors"
                                    >
                                        Back to service
                                        <ArrowUpRight className="w-4 h-4" />
                                    </Link>
                                </div>
                                {cases.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {cases.map((c) => (
                                            <div
                                                key={c.slug}
                                                data-testid={`pillar-case-${p.anchor}-${c.slug}`}
                                                className="group relative border border-brand-primary/10 bg-brand-bg flex flex-col h-full"
                                            >
                                                <div className="relative overflow-hidden">
                                                    <div
                                                        className="aspect-[16/10] w-full bg-cover bg-center transition-transform duration-[1200ms] group-hover:scale-105"
                                                        style={{ backgroundImage: `url(${c.image})` }}
                                                    />
                                                </div>
                                                <div className="p-6 sm:p-8 flex flex-col gap-3">
                                                    <div className="flex items-center justify-between gap-4">
                                                        <p className="overline">{c.tag}</p>
                                                        <span className="font-mono text-xs text-brand-primary uppercase tracking-widest">{c.metric}</span>
                                                    </div>
                                                    <h4 className="font-display text-2xl text-brand-mist tracking-tight">{c.title}</h4>
                                                    <p className="text-brand-frost/85 leading-relaxed">{c.excerpt}</p>
                                                    <p className="text-brand-frost/70 text-sm border-t border-brand-primary/10 pt-3 mt-1">{c.client}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p
                                        data-testid={`pillar-placeholder-${p.anchor}`}
                                        className="text-brand-frost/70 italic"
                                    >
                                        Case studies coming soon.
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* CASE STUDIES — deep-dive expandable cards */}
            <section
                id="case-studies"
                data-testid="case-studies"
                className="border-t border-brand-primary/10 py-24 lg:py-32"
            >
                <div className="container-x">
                    <Reveal className="mb-16 max-w-4xl">
                        <p className="overline mb-6">Case studies</p>
                        <h2 className="font-display h-display-lg">
                            The full stories,{" "}
                            <span className="italic text-brand-primary">told in detail.</span>
                        </h2>
                        <p className="mt-8 text-brand-frost/85 leading-relaxed max-w-2xl">
                            Deep-dive briefs, our approach, and the numbers we shipped for each partner. Click any card to expand the full case study.
                        </p>
                    </Reveal>

                    <div className="space-y-6">
                        {CASE_STUDIES.map((cs, i) => (
                            <Reveal key={cs.slug} delay={i * 60}>
                                <details
                                    id={cs.slug}
                                    data-testid={`case-study-${cs.slug}`}
                                    className="group border border-brand-primary/15 bg-brand-surface/50 open:bg-brand-surface open:border-brand-primary/30 transition-colors"
                                >
                                    <summary className="cursor-pointer list-none p-6 sm:p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                                        <div className="md:w-1/3 lg:w-1/4">
                                            <div className="relative aspect-[16/10] overflow-hidden bg-brand-bg">
                                                <img
                                                    src={cs.image}
                                                    alt={`${cs.client} · ${cs.service}`}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex-1 flex flex-col gap-3">
                                            <p className="overline">{cs.service}</p>
                                            <h3 className="font-display text-2xl lg:text-3xl text-brand-mist tracking-tight">
                                                {cs.client}
                                            </h3>
                                            <p className="text-brand-frost/80 leading-relaxed line-clamp-2 md:line-clamp-none">
                                                {cs.brief}
                                            </p>
                                        </div>
                                        <div className="shrink-0 self-start md:self-center">
                                            <span
                                                aria-hidden="true"
                                                className="inline-flex items-center justify-center w-11 h-11 border border-brand-primary/40 text-brand-primary transition-transform group-open:rotate-45 text-2xl"
                                            >
                                                +
                                            </span>
                                        </div>
                                    </summary>

                                    <div className="px-6 sm:px-8 lg:px-10 pb-10 lg:pb-12 space-y-10 border-t border-brand-primary/10 pt-8">
                                        <div>
                                            <p className="overline mb-4">About the company</p>
                                            <p className="text-brand-frost/85 leading-relaxed max-w-3xl">
                                                {cs.about}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="overline mb-4">{cs.briefLabel}</p>
                                            <p className="text-brand-frost/85 leading-relaxed max-w-3xl">
                                                {cs.brief}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="overline mb-6">{cs.solutionLabel || "Our Solution"}</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                                                {cs.solution.map((step) => (
                                                    <div key={step.title} className="border-t border-brand-primary/15 pt-4">
                                                        <h4 className="font-display text-lg text-brand-mist mb-2 tracking-tight">
                                                            {step.title}
                                                        </h4>
                                                        <p className="text-sm text-brand-frost/80 leading-relaxed">
                                                            {step.copy}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <p className="overline mb-6">Results</p>
                                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                                                {cs.results.map((r) => (
                                                    <div key={r.label} className="border-t border-brand-primary/20 pt-4">
                                                        <p className="font-display text-3xl lg:text-4xl text-brand-mist tracking-tight">{r.value}</p>
                                                        <p className="text-xs text-brand-primary/70 uppercase tracking-wider mt-2">{r.label}</p>
                                                    </div>
                                                ))}
                                            </div>
                                            {cs.resultsFootnote ? (
                                                <p className="mt-8 text-brand-frost/75 leading-relaxed max-w-3xl italic">
                                                    {cs.resultsFootnote}
                                                </p>
                                            ) : null}
                                        </div>

                                        <div className="border-t border-brand-primary/15 pt-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                                            <div className="max-w-2xl">
                                                <h4 className="font-display h-display-sm text-brand-mist mb-3">
                                                    {cs.ctaHeading}
                                                </h4>
                                                <p className="text-brand-frost/85 leading-relaxed">
                                                    {cs.ctaBody}
                                                </p>
                                            </div>
                                            <Link
                                                to="/contact"
                                                data-testid={`case-study-cta-${cs.slug}`}
                                                className="btn-primary shrink-0"
                                            >
                                                {cs.ctaButton} <ArrowUpRight className="w-4 h-4" />
                                            </Link>
                                        </div>
                                    </div>
                                </details>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 border-t border-brand-primary/10">
                <div className="container-x flex flex-col lg:flex-row items-start lg:items-end justify-between gap-10">
                    <Reveal>
                        <h2 className="font-display h-display-lg max-w-3xl">
                            Your firm,
                            <br />
                            <span className="italic text-brand-primary">on this page next.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={120}>
                        <Link to="/contact" className="btn-primary">
                            Start the conversation <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
